import { Button as HeroButton } from '@components/Button'
import { HeroIcon } from '@components/HeroIcon'
import { ProgressCircle } from '@components/ProgressCircle'
import { ScrollBar } from '@components/ScrollBar'
import { useCollapsibleToggle } from '@hooks/useCollapsibleToggle'
import { useCourse } from '@hooks/useCourse'
import { useEnrollment } from '@hooks/useEnrollment'
import { useModules } from '@hooks/useModules'
import { Portal } from '@tamagui/portal'
import { useLocalSearchParams } from 'expo-router'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, Button, Text, View, XStack, YStack } from 'tamagui'
import { ContentsModules } from './ContentsModules'
import { ContentProgress } from './ContenProgress'

interface IContentsCollapsibleWebProps {
  type: 'bottomFixed' | 'topOpened'
}

export function ContentsCollapsibleWeb({
  type = 'bottomFixed',
}: IContentsCollapsibleWebProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const { t } = useTranslation('CourseDetails')
  const [scrollBarHeight, setScrollBarHeight] = useState(0)

  const { modules, onLoadMore } = useModules()
  const { enrollment } = useEnrollment()
  const { course } = useCourse()
  const { moduleId } = useLocalSearchParams()
  const modalChevronIcon = isExpanded ? 'chevron-down' : 'chevron-up'

  const { isCollapsibleOpen, toggleCollapsible } = useCollapsibleToggle()

  const togglePosition = () => {
    setIsExpanded(!isExpanded)
  }

  const filteredModules = useMemo(() => {
    if (!modules?.nodes) return []
    return modules.nodes.filter((module) => module.available !== false)
  }, [modules])

  const renderModules = useMemo(() => {
    return filteredModules.map((module, index) => {
      const isModuleOpen = module.id.toString() === moduleId
      const isLastModule = index === filteredModules.length - 1
      return (
        <ContentsModules
          key={module.id}
          module={module}
          moduleId={moduleId}
          onLoadMore={onLoadMore}
          isLastModule={isLastModule}
          isModuleOpen={isModuleOpen}
        />
      )
    })
  }, [filteredModules, moduleId, onLoadMore])

  const iconColor = isActive ? '$primary-300' : '$neutral-500'

  if (type === 'topOpened') {
    return (
      <AnimatePresence>
        {isCollapsibleOpen && (
          <YStack
            key="collapsible-web-top-opened"
            animation="quick"
            enterStyle={{
              opacity: 0,
              x: 100,
            }}
            exitStyle={{
              opacity: 0,
              x: 100,
            }}
            opacity={1}
            x={0}
            minWidth={isCollapsibleOpen ? 407 : 0}
            width={isCollapsibleOpen ? '25%' : '0%'}
            backgroundColor="$neutral-0"
            height={630}
            borderRadius={'$border-radius-md'}
            overflow="hidden"
            flexShrink={0}
            marginLeft="auto"
          >
            <YStack padding="$spacing-inset-sm" gap="$spacing-inset-xs">
              <XStack justifyContent="flex-end">
                <HeroButton
                  variant="primary"
                  testID="header-back-button"
                  aria-label="Back"
                  link
                  paddingLeft={0}
                  onPress={toggleCollapsible}
                >
                  <XStack alignItems="center">
                    <HeroIcon
                      icon={['fal', 'chevron-left']}
                      color="$primary-300"
                      size={16}
                    />
                    <Text
                      fontSize="$font-size-xxs"
                      ff="$mulishRegular"
                      color="$primary-300"
                      position="relative"
                      textDecorationLine="underline"
                    >
                      {t('Esconder Lista')}
                    </Text>
                  </XStack>
                </HeroButton>
              </XStack>
              <XStack>
                <Text
                  color="$neutral-800"
                  fontSize="$font-size-xs"
                  fontFamily="$mulishSemiBold"
                  fontWeight="600"
                  width="90%"
                  numberOfLines={3}
                >
                  {course?.name}
                </Text>
              </XStack>
              <XStack alignItems="center" gap="$spacing-inset-quarck">
                <ProgressCircle
                  progress={enrollment?.progressCard?.progress || 0}
                />
                {enrollment && <ContentProgress enrollment={enrollment} />}
              </XStack>
            </YStack>
            <YStack
              flex={1}
              overflow="hidden"
              padding="$spacing-inset-sm"
              paddingTop={0}
              onLayout={(event) =>
                setScrollBarHeight(event.nativeEvent.layout.height)
              }
            >
              <ScrollBar
                maxHeight={scrollBarHeight}
                displayNativeIndicator={false}
                scrollBarId="contents-modal-scroll"
              >
                {renderModules}
              </ScrollBar>
            </YStack>
            <XStack
              h={56}
              borderTopWidth={1}
              borderColor="$neutral-200"
              marginTop="auto"
            />
          </YStack>
        )}
      </AnimatePresence>
    )
  }

  return (
    <Portal>
      <View
        tag="div"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99999999999,
          width: '100%',
        }}
        pointerEvents="box-none"
      >
        <YStack
          width="100%"
          height={isExpanded ? '80%' : 120}
          backgroundColor="transparent"
          pointerEvents="auto"
        >
          <AnimatePresence>
            <YStack
              key="collapsible-content"
              animation="quick"
              enterStyle={{
                opacity: 0,
                y: 100,
              }}
              exitStyle={{
                opacity: 0,
                y: 100,
              }}
              opacity={1}
              y={0}
              backgroundColor="$neutral-0"
              borderTopLeftRadius="$border-radius-xlg"
              borderTopRightRadius="$border-radius-xlg"
              height={isExpanded ? '100%' : 120}
              pointerEvents="auto"
              overflow="hidden"
            >
              <XStack
                p="$spacing-inset-xs"
                gap="$spacing-inset-nano"
                alignItems="center"
                height={100}
              >
                <ProgressCircle
                  progress={enrollment?.progressCard?.progress || 0}
                />
                <YStack flex={1} gap="$spacing-inset-quarck">
                  <Text
                    color="$neutral-800"
                    fontSize="$font-size-xs"
                    fontFamily="$mulishSemiBold"
                    fontWeight="600"
                    width="90%"
                    numberOfLines={3}
                  >
                    {course?.name}
                  </Text>
                  {enrollment && <ContentProgress enrollment={enrollment} />}
                </YStack>
                <Button
                  onPress={togglePosition}
                  backgroundColor="transparent"
                  size={32}
                  onHoverIn={() => setIsActive(true)}
                  onHoverOut={() => setIsActive(false)}
                  onPressIn={() => setIsActive(true)}
                  onPressOut={() => setIsActive(false)}
                  hoverStyle={{
                    backgroundColor: '$neutral-200',
                    outlineWidth: 0,
                  }}
                  pressStyle={{
                    scale: 0.97,
                    backgroundColor: '$neutral-200',
                    outlineWidth: 0,
                  }}
                  focusStyle={{ outlineWidth: 0 }}
                >
                  <HeroIcon
                    icon={['fal', modalChevronIcon]}
                    size={16}
                    color={iconColor}
                  />
                </Button>
              </XStack>
              {!isExpanded && (
                <YStack
                  key="separator"
                  backgroundColor="$neutral-0"
                  borderColor="$neutral-200"
                  borderWidth={1}
                  marginHorizontal="$spacing-inset-xs"
                />
              )}

              <YStack
                overflow="hidden"
                flex={1}
                padding="$spacing-inset-xs"
                onLayout={(event) =>
                  setScrollBarHeight(event.nativeEvent.layout.height)
                }
              >
                <ScrollBar
                  maxHeight={scrollBarHeight}
                  displayNativeIndicator={false}
                  scrollBarId="contents-modal-scroll"
                >
                  {renderModules}
                </ScrollBar>
              </YStack>
            </YStack>
          </AnimatePresence>
        </YStack>
      </View>
    </Portal>
  )
}
